import { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import PageLoader from "./component/PageLoader";
import { retrieveFromLocal } from "./helper/localStore";
import { storeRedirectPath } from "./helper/authRedirect";

// Lazy load pages
const Login = lazy(() => import("./page/Login"));
const NotFound = lazy(() => import("./page/NotFound"));

// Protected Pages (Require Permissions)
const routesWithPermissions = [
  {
    path: "/word-dictionary",
    component: lazy(() => import("./page/Dictionary")),
    feature: "all",
  },
  {
    path: "/text-to-speech",
    component: lazy(() => import("./page/TextToSpeech")),
    feature: "text_to_speech",
  },
  {
    path: "/text-to-speech-voice-cloning",
    component: lazy(() => import("./page/TextToSpeechVoiceOver")),
    feature: "text_to_speech_with_voice_over",
  },
  {
    path: "/voice-cloning",
    component: lazy(() => import("./page/VoiceCloning")),
    feature: "voice_cloning",
  },
  {
    path: "/text-to-text",
    component: lazy(() => import("./page/TextToText")),
    feature: "text_translation",
  },
  {
    path: "/speech-to-speech",
    component: lazy(() => import("./page/SpeechToSpeech")),
    feature: "speech_translation",
  },
  {
    path: "/real-time-stt",
    component: lazy(() => import("./page/RealTimeSTT")),
    feature: "text_to_speech",
  },
  {
    path: "/real-time-sts",
    component: lazy(() => import("./page/RealTimeSTS")),
    feature: "text_to_speech",
  },
  {
    path: "/real-time-asws",
    component: lazy(() => import("./page/AudioStreamTranslation")),
    feature: "real_time_audio_stream_with_speech",
  },
  {
    path: "/speech-to-text",
    component: lazy(() => import("./page/SpeechToText")),
    feature: "speech_to_text",
  },
  {
    path: "/video-subtitle",
    component: lazy(() => import("./page/VideoSubtitle")),
    feature: "video_subtitle",
  },
  {
    path: "/image-to-video",
    component: lazy(() => import("./page/ImageToVideo")),
    feature: "image_to_video",
  },
  {
    path: "/multi-speaker-ttt",
    component: lazy(() => import("./page/MultiSpeakerTTT")),
    feature: "ms_ttt",
  },
  {
    path: "/multi-speaker-tts-w-vc",
    component: lazy(() => import("./page/MultiSpeakerTTSWVC")),
    feature: "ms_tts_w_vc",
  },
  {
    path: "/multi-speaker-stt",
    component: lazy(() => import("./page/MultiSpeakerSTT")),
    feature: "ms_stt",
  },
  {
    path: "/multi-speaker-sts-w-vc",
    component: lazy(() => import("./page/MultiSpeakerSTSWVC")),
    feature: "ms_sts_w_vc",
  },
  {
    path: "/video-translation",
    component: lazy(() => import("./page/VideoTranslation")),
    feature: "video_translation",
  },
  {
    path: "/video-translation-w-vc",
    component: lazy(() => import("./page/VideoTranslationWVC")),
    feature: "video_translation_w_vc",
  },
  {
    path: "/podcast",
    component: lazy(() => import("./page/MultiSpeakerVTWVC")),
    feature: "ms_vt_w_vc",
  },
  {
    path: "/instant-voice-cloning",
    component: lazy(() => import("./page/InstantVoiceCloning")),
    feature: "instant_voice_cloning",
  },
  {
    path: "/lms-vt",
    component: lazy(() => import("./page/LmsVT")),
    feature: "lms_vt",
  },
  {
    path: "/reported-issue",
    component: lazy(() => import("./page/ReportedIssue")),
    feature: "all",
  },
  {
    path: "/api-keys",
    component: lazy(() => import("./page/ApiKeys")),
    feature: "all",
  },
  {
    path: "/shared/:type/:resourceId",
    component: lazy(() => import("./page/Shared")),
    feature: "all",
  },
];

const hasPermission = (permissions: any, feature: string) =>
  permissions?.some((p: any) => p.feature === feature || feature === "all");

function App() {
  const token: any = retrieveFromLocal("token");
  const permissions: any = retrieveFromLocal("permissions");

  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<Login />} />

          {token ? (
            <>
              {routesWithPermissions.map(
                ({ path, component: Component, feature }) =>
                  hasPermission(permissions, feature) ? (
                    <Route key={path} path={path} element={<Component />} />
                  ) : null
              )}
              <Route path="*" element={<NotFound />} />
            </>
          ) : (
            <Route
              path="*"
              element={(() => {
                storeRedirectPath();
                return <Navigate to="/login" replace />;
              })()}
            />
          )}
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
